import { useState, useEffect, useCallback } from 'react';
import { groupMasterApi } from '../api/groupMasterApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

const REPORT_COLUMNS = [
  { key: 'name', label: 'Group Name' },
  { key: 'weeklyOff', label: 'Weekly Off' },
  { key: 'secondWeeklyOff', label: 'Second Weekly Off' },
  { key: 'status', label: 'Status' },
];

export function useGroupMasterList() {
  const [nameFilter, setNameFilter] = useState('');
  const [weeklyOffFilter, setWeeklyOffFilter] = useState('Select');
  const [secondWeeklyOffFilter, setSecondWeeklyOffFilter] = useState('Select');
  const [statusFilter, setStatusFilter] = useState('Active');

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { triggerExport, isExporting } = useExportJob({
    module: 'employee-group',
    jobType: 'EXPORT',
  });

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await groupMasterApi.getAll({
        name: nameFilter,
        weeklyOff: weeklyOffFilter,
        secondWeeklyOff: secondWeeklyOffFilter,
        status: statusFilter,
        page,
        pageSize,
      });

      const items = (data.items || []).map((item) => ({
        id: item.id,
        name: item.name,
        weeklyOff: item.weekly_off,
        secondWeeklyOff: item.second_weekly_off,
        archive: item.archive,
        status: item.status,
      }));

      setRecords(items);
      setTotalCount(data.total_count || items.length);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error(err);
      appToast.error('Failed to load Employee Group records.');
    } finally {
      setIsLoading(false);
    }
  }, [nameFilter, weeklyOffFilter, secondWeeklyOffFilter, statusFilter, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setNameFilter('');
    setWeeklyOffFilter('Select');
    setSecondWeeklyOffFilter('Select');
    setStatusFilter('Active');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'print') {
      executePrintReport({
        title: 'Employee Group Directory',
        subtitle: `Status: ${statusFilter} | Total Records: ${totalCount}`,
        columns: REPORT_COLUMNS,
        data: records,
        onExportPdf: () => triggerExport('pdf', { name: nameFilter, weeklyOff: weeklyOffFilter, secondWeeklyOff: secondWeeklyOffFilter, status: statusFilter }),
        onExportExcel: () => triggerExport('xlsx', { name: nameFilter, weeklyOff: weeklyOffFilter, secondWeeklyOff: secondWeeklyOffFilter, status: statusFilter }),
      });
    } else if (toolKey === 'export_excel') {
      triggerExport('xlsx', { name: nameFilter, weeklyOff: weeklyOffFilter, secondWeeklyOff: secondWeeklyOffFilter, status: statusFilter });
    } else if (toolKey === 'export_pdf') {
      triggerExport('pdf', { name: nameFilter, weeklyOff: weeklyOffFilter, secondWeeklyOff: secondWeeklyOffFilter, status: statusFilter });
    }
  };

  const handleArchive = async (item) => {
    const isCurrentlyActive = item.status === 'Active';
    const actionText = isCurrentlyActive ? 'Archive' : 'Activate';

    const confirmed = await appConfirm({
      title: `${actionText} Employee Group`,
      message: `Are you sure you want to ${actionText.toLowerCase()} group "${item.name}"?`,
      confirmText: actionText,
      confirmVariant: isCurrentlyActive ? 'warning' : 'primary',
    });

    if (!confirmed) return;

    try {
      const res = await groupMasterApi.archive(item.id);
      appToast.success(res.message || `Group "${item.name}" ${actionText.toLowerCase()}d successfully.`);
      loadRecords();
    } catch (err) {
      console.error(err);
      appToast.error(`Failed to ${actionText.toLowerCase()} group.`);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Employee Group',
      message: `Are you sure you want to delete group "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });

    if (!confirmed) return;

    try {
      await groupMasterApi.delete(id);
      appToast.success(`Group "${name}" deleted successfully.`);
      loadRecords();
    } catch (err) {
      console.error(err);
      appToast.error('Failed to delete group.');
    }
  };

  const tools = [
    { key: 'print', label: 'Print Report', icon: 'print' },
    { key: 'export_excel', label: 'Export Excel', icon: 'description' },
    { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  ];

  return {
    state: {
      records,
      isLoading,
      nameFilter,
      searchText: nameFilter,
      weeklyOffFilter,
      secondWeeklyOffFilter,
      statusFilter,
      page,
      pageSize,
      totalCount,
      totalPages,
      tools,
      isExporting,
    },
    actions: {
      setNameFilter,
      setSearchText: setNameFilter,
      setWeeklyOffFilter,
      setSecondWeeklyOffFilter,
      handleStatusTabChange,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
      handleArchive,
      handleDelete,
    },
  };
}

